import {
  Button,
  Input,
  Layout,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { blue, red } from '@ant-design/colors';
import keyBy from 'lodash/keyBy';
import { models } from '@ynomia/core';
import ModelEditor, { ApsModel } from '../../components/connected/ModelEditor';
import {
  getAssetTypes,
  getAssetTypesById,
  getScratchProjectCode,
  getTenant,
} from '../../selectors';
import { getAssets, getLayers } from '../../actions';
import ModelSetupModal, { SetupStepsEnum } from '../../components/connected/ModelSetupModal';
import { describeStatus } from '../../components/connected/ModelEditor/modelStatus';
import { PageProps } from '../../config/types';
import client from '../../services/Client';
import { formatDate } from '../../utils';
import { getContextStores } from '../../context';
import { notification } from '../../antdProvider';
import styles from './styles.module.less';
import * as digitalTwin from '../../services/digitalTwin';

const { Content } = Layout;

const Models: FC<PageProps> = () => {
  const contextStores = getContextStores();
  const {
    assetDispatch,
    assetState,
    layerDispatch,
    layerState,
  } = contextStores;
  // Upload Modal States
  const [isSetupModalOpen, setIsSetupModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  // Models
  const [fetchingModels, setFetchingModels] = useState<boolean>(true);
  const [firstFetch, setFirstFetch] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [apsModels, setApsModels] = useState<Array<ApsModel>>([]);
  const [modelsData, setModelsData] = useState<models.ModelResponse[]>();
  const [togglingModels, setTogglingModels] = useState<{ [objectKey: string]: boolean }>({});
  const [savingInProgress, setSavingInProgress] = useState<boolean>(false);
  const modelsKeyedById = keyBy(modelsData, 'id');

  const [stepOverride, setInitialStep] = useState<number>(0);
  const [focusedModelId, setFocusedModelId] = useState<string | undefined>(undefined);

  /* Selectors */
  const projectCode = getScratchProjectCode(contextStores);
  const tenant = getTenant(contextStores);

  const assetTypes = getAssetTypes(contextStores);
  const assetTypesKeyedById = getAssetTypesById(contextStores);

  const { lastFetchStartTime } = assetState;

  const apsModelsKeyedByObjectKey = keyBy(apsModels, 'objectKey');

  /** Each refresh's number, so a response that a newer refresh has overtaken can be dropped. */
  const latestRefresh = useRef(0);

  const refreshModels = async (fetchAPS = true) => {
    latestRefresh.current += 1;
    const refreshId = latestRefresh.current;
    // A newer refresh started while this one was in flight, e.g. the one after a save or
    // re-map. Its data is fresher, so this snapshot mustn't overwrite it.
    const isLatest = () => refreshId === latestRefresh.current;
    setFetchingModels(true);

    try {
      const fetchedModels = await digitalTwin.fetchModels(
        tenant,
        projectCode,
        {
          activeOnly: false,
          includeMapping: false,
        },
      );

      let fetchedApsModels: Array<ApsModel> | undefined;
      if (fetchAPS) {
        try {
          const listResp = await client.server.get(
            `/scratch/models/${tenant}/${projectCode}/list`,
          );
          fetchedApsModels = listResp.data.value;
        } catch (e) {
          // The model list still updates without it; the next refresh tries again.
        }
      }

      if (!isLatest()) return;
      // Only update the UI once everything is done.
      if (fetchedApsModels) setApsModels(fetchedApsModels);
      setModelsData(fetchedModels);
      // Only once models have actually loaded: Add Model checks new names against them.
      setFirstFetch(false);
    } catch (e: any) {
      if (!isLatest()) return;
      // Keyed, so a run of failed polls shows one notification rather than a stack of them.
      notification.error({
        key: 'refresh-models',
        message: 'Could not load models',
        description: e?.response?.data?.err?.msg || e?.message,
      });
    } finally {
      if (isLatest()) setFetchingModels(false);
    }
  };

  const updateModel = async (
    id: string,
    body: Partial<models.Model>,
  ) => {
    setSavingInProgress(true);
    try {
      await digitalTwin.patchModel(tenant, projectCode, id, body);
      await refreshModels(false);
    } finally {
      setSavingInProgress(false);
    }
  };

  const remapModel = async (id: string) => {
    try {
      await digitalTwin.remapModel(tenant, projectCode, id);
    } catch (e: any) {
      notification.error({
        message: 'Could not re-map the model',
        description: e?.response?.data?.err?.msg || e?.message,
      });
    }
    await refreshModels(false);
  };

  const createModel = async (
    body: Partial<models.Model>,
  ) => {
    setSavingInProgress(true);
    try {
      const res = await digitalTwin.createModel(tenant, projectCode, body);
      await refreshModels();
      return res;
    } finally {
      setSavingInProgress(false);
    }
  };

  useEffect(() => {
    // Need this to visualise twinID mapping
    getAssets(tenant, projectCode, assetTypes, lastFetchStartTime, assetDispatch);
    getLayers(tenant, projectCode, layerState.lastFetchStartTime, layerDispatch);
  }, []);

  useEffect(() => {
    refreshModels();
    const t = setInterval(refreshModels, 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const deleteModel = async (id: string) => {
    const modelToBeDeleted = modelsData?.find(m => m.id === id);
    if (!modelToBeDeleted) return;
    const objectKey = modelToBeDeleted?.fileName;
    await updateModel(id, { isArchived: true });
    if (apsModelsKeyedByObjectKey[objectKey]) {
      await client.server.delete(
        `/scratch/models/${tenant}/${projectCode}/object/${objectKey}`,
      );
    }
    await refreshModels();
  };

  const getModelStatusInfo = (id: string, m: ApsModel | undefined): {
    step: number;
    sortOrder: number;
    fillColor: string;
    tooltipMessage: string;
    icon: React.ReactNode;
  } => {
    const failed = red[6];
    const normal = blue[6];
    const model = modelsKeyedById[id];
    const status = describeStatus(model?.status);

    if (status) {
      const { label } = status;
      switch (model.status) {
        case 'draft':
          return {
            step: SetupStepsEnum.selectFile,
            sortOrder: 0,
            fillColor: normal,
            tooltipMessage: label,
            icon: null,
          };
        case 'translation_failed':
        case 'mapping_failed':
          return {
            step: SetupStepsEnum.processing,
            sortOrder: 2,
            fillColor: failed,
            tooltipMessage: model.statusError ? `${label}: ${model.statusError}` : label,
            icon: <CloseCircleOutlined />,
          };
        case 'mapped':
          return {
            step: SetupStepsEnum.finish,
            sortOrder: model.active ? 5 : 4,
            fillColor: normal,
            tooltipMessage: model.active
              ? 'Published (Mapped)'
              : 'Enable the model to publish (Mapped)',
            icon: <CheckCircleOutlined />,
          };
        default: {
          const progress = model.status === 'translating' && m?.manifest?.progress;
          return {
            step: SetupStepsEnum.processing,
            sortOrder: 1,
            fillColor: normal,
            tooltipMessage: progress ? `${label} - ${progress}` : label,
            icon: <LoadingOutlined />,
          };
        }
      }
    }

    // Models from before statuses existed, until the migration gives them one.
    if (!m) {
      return {
        step: SetupStepsEnum.selectFile,
        sortOrder: 0,
        fillColor: normal,
        tooltipMessage: '',
        icon: null,
      };
    }

    if (!m.manifest || m.manifest.status === 'failed') {
      return {
        step: SetupStepsEnum.processing,
        sortOrder: 2,
        fillColor: failed,
        tooltipMessage: 'Failed to translate',
        icon: <CloseCircleOutlined />,
      };
    }

    if (m.manifest?.progress !== 'complete') {
      return {
        step: SetupStepsEnum.processing,
        sortOrder: 1,
        fillColor: normal,
        tooltipMessage: `Translating - ${m.manifest?.progress}`,
        icon: <LoadingOutlined />,
      };
    }

    if (!model?.isMapped) {
      return {
        step: SetupStepsEnum.processing,
        sortOrder: 3,
        fillColor: normal,
        tooltipMessage: 'Not yet mapped.',
        icon: <ExclamationCircleOutlined />,
      };
    }

    return {
      step: SetupStepsEnum.finish,
      sortOrder: model.active ? 5 : 4,
      fillColor: normal,
      tooltipMessage: model.active
        ? 'Published (Mapped)'
        : 'Enable the model to publish (Mapped)',
      icon: <CheckCircleOutlined />,
    };
  };

  const generateStatus = (
    id: string,
    m: ApsModel,
    statusInfo: ReturnType<typeof getModelStatusInfo>,
  ) => {
    if (!m) {
      return <div style={{ textAlign: 'center' }}>Not on APS</div>;
    }

    const totalSteps = 4;
    const {
      step, fillColor, tooltipMessage, icon,
    } = statusInfo;
    const strokeColor = new Array(totalSteps).fill(fillColor);
    return (
      <div style={{ textAlign: 'center' }}>
        <Tooltip title={tooltipMessage} style={{ alignSelf: 'center' }}>
          <div
            onClick={() => {
              setFocusedModelId(id);
              setInitialStep(step);
              setIsSetupModalOpen(true);
            }}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
          >
            <Progress
              steps={totalSteps}
              percent={((step + 1) / totalSteps) * 100}
              strokeColor={strokeColor}
              format={() => icon}
            />
          </div>
        </Tooltip>
      </div>
    );
  };

  type ModelRow = {
    key: string;
    name: string;
    originalFileName?: string;
    assetTypes?: string;
    twinIdField?: string;
    statusSortOrder: number;
  };

  const assetTypeFilters = assetTypes.map(({ id, name }) => ({ text: name, value: id }));
  const twinIdFieldFilters = [...new Set(
    (modelsData || []).map(m => m.twinIdField).filter(Boolean),
  )].map(field => ({ text: field, value: field }));

  const tableColumns = [
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      width: 80,
      sorter: (a: ModelRow, b: ModelRow) => (
        Number(modelsKeyedById[a.key]?.active) - Number(modelsKeyedById[b.key]?.active)
      ),
      filters: [
        { text: 'Enabled', value: true },
        { text: 'Disabled', value: false },
      ],
      onFilter: (value: React.Key | boolean, record: ModelRow) => (
        Boolean(modelsKeyedById[record.key]?.active) === value
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: ModelRow, b: ModelRow) => String(a.name).localeCompare(String(b.name)),
    },
    {
      title: 'Upload File',
      dataIndex: 'originalFileName',
      sorter: (a: ModelRow, b: ModelRow) => (
        String(a.originalFileName || '').localeCompare(String(b.originalFileName || ''))
      ),
    },
    {
      title: 'Asset Type(s)',
      dataIndex: 'assetTypes',
      sorter: (a: ModelRow, b: ModelRow) => String(a.assetTypes || '').localeCompare(
        String(b.assetTypes || ''),
      ),
      filters: assetTypeFilters,
      filterSearch: true,
      onFilter: (value: React.Key | boolean, record: ModelRow) => (
        modelsKeyedById[record.key]?.assetTypes?.includes(value as string) ?? false
      ),
    },
    {
      title: 'TwinID Field',
      dataIndex: 'twinIdField',
      sorter: (a: ModelRow, b: ModelRow) => (
        String(a.twinIdField || '').localeCompare(String(b.twinIdField || ''))
      ),
      filters: twinIdFieldFilters,
      filterSearch: true,
      onFilter: (value: React.Key | boolean, record: ModelRow) => record.twinIdField === value,
    },
    {
      title: 'Upload Date',
      dataIndex: 'date',
      sorter: (a: ModelRow, b: ModelRow) => (
        new Date(modelsKeyedById[a.key]?.date || 0).getTime()
        - new Date(modelsKeyedById[b.key]?.date || 0).getTime()
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      sorter: (a: ModelRow, b: ModelRow) => a.statusSortOrder - b.statusSortOrder,
    },
    {
      title: 'Actions',
      dataIndex: 'action',
      width: 140,
    },
  ];

  const setModelEnabled = async (id: string, enable: boolean) => {
    setTogglingModels(s => ({ ...s, [id]: true }));
    try {
      await updateModel(id, { active: enable });
    } catch (e: any) {
      notification.error({
        message: `Could not ${enable ? 'enable' : 'disable'} the model`,
        description: e?.response?.data?.err?.msg || e?.message,
      });
    } finally {
      setTogglingModels(s => ({ ...s, [id]: false }));
    }
  };

  const generateActions = (id: string, disableDeleteButton?: boolean) => (
    <Space size="middle" style={{ justifyContent: 'start', width: '100%' }}>
      <Typography.Link
        onClick={() => {
          setFocusedModelId(id);
          setIsEditModalOpen(true);
        }}
      >
        Edit
      </Typography.Link>
      <Popconfirm
        title="Are you sure you want to delete?"
        onConfirm={() => deleteModel(id)}
      >
        <Typography.Link
          disabled={disableDeleteButton}
          type="danger"
        >
          Delete
        </Typography.Link>
      </Popconfirm>
    </Space>
  );

  // Scratch returns models oldest first; reversed so a model just added is at the top.
  const rows = Object.keys(modelsKeyedById).reverse().map((id) => {
    const model = modelsKeyedById[id];
    const apsModel = apsModelsKeyedByObjectKey[model.fileName];
    const statusInfo = getModelStatusInfo(id, apsModel);
    const { fileName } = model;
    const enabled = model.active;
    const disableToggle = savingInProgress
      || !model.urn
      || model.status === 'translating'
      // Scratch only sends the manifest of a model that is translating, failed to translate or
      // has no status. An uploaded file has no manifest until a translation starts on it.
      || (!model.status && apsModel && apsModel.manifest?.progress !== 'complete');
    return ({
      key: id,
      name: fileName,
      enabled: <Switch
        disabled={disableToggle}
        checked={enabled}
        onClick={() => setModelEnabled(id, !enabled)}
        loading={togglingModels[id]}
      />,
      status: generateStatus(id, apsModel, statusInfo),
      statusSortOrder: statusInfo.sortOrder,
      action: generateActions(id, enabled || savingInProgress),
      assetTypes: model.assetTypes?.map(t => assetTypesKeyedById.get(t)?.name?.toLowerCase())
        .join(', '),
      twinIdField: model.twinIdField,
      originalFileName: model.originalFileName,
      date: formatDate(model.date),
    });
  });

  /** The columns shown as text. Status and the controls are icons and buttons. */
  const SEARCHABLE_COLUMNS = ['name', 'originalFileName', 'assetTypes', 'twinIdField', 'date'];
  const searchTerm = search.trim().toLowerCase();
  const dataSource = searchTerm
    ? rows.filter(row => SEARCHABLE_COLUMNS.some(column => (
      String(row[column] ?? '').toLowerCase().includes(searchTerm)
    )))
    : rows;

  return (
    <Content className={styles.container}>
      <div className={styles.header}>
        <Typography.Title level={3} style={{ flex: 1 }}>
          Current Models
        </Typography.Title>
        <Input
          placeholder="Search"
          prefix={<SearchOutlined />}
          allowClear
          value={search}
          onChange={e => setSearch(e.target.value)}
          // The header row stretches its children; the buttons have a fixed height, this doesn't.
          style={{
            width: 250,
            marginTop: 24,
            marginRight: 10,
            alignSelf: 'flex-start',
          }}
        />
        <Button
          type="primary"
          icon={<SyncOutlined />}
          style={{ marginTop: 24, marginRight: 10 }}
          size="middle"
          onClick={() => refreshModels()}
          loading={fetchingModels}
        />
        <Button
          type="primary"
          onClick={() => {
            // Open Modal
            setFocusedModelId(undefined);
            setInitialStep(0);
            setIsSetupModalOpen(true);
          }}
          style={{ width: 100, marginTop: 24 }}
          // Checks the new model's name against existing ones, so it needs them loaded first.
          disabled={firstFetch}
        >
          Add Model
        </Button>
      </div>
      <Table
        columns={tableColumns}
        dataSource={dataSource}
        loading={firstFetch}
        scroll={{ x: 'max-content' }}
      />
      <ModelSetupModal
        modelIdOverride={focusedModelId}
        modelsKeyedById={modelsKeyedById}
        apsModels={apsModels}
        savingInProgress={savingInProgress}
        isSetupModalOpen={isSetupModalOpen}
        stepOverride={stepOverride}
        setIsSetupModalOpen={setIsSetupModalOpen}
        onPreview={(id) => {
          setIsSetupModalOpen(false);
          setFocusedModelId(id);
          setIsEditModalOpen(true);
        }}
        remapModel={remapModel}
        updateModel={async (id, options) => {
          await updateModel(id, options);
          await refreshModels(true);
        }}
        createModel={createModel}
      />
      <Modal
        title={`Edit — ${modelsKeyedById[focusedModelId!]?.fileName}`}
        open={focusedModelId !== undefined && isEditModalOpen}
        width={1000}
        footer={null}
        maskClosable={false}
        onCancel={() => setIsEditModalOpen(false)}
        destroyOnClose
      >
        <ModelEditor
          modelId={focusedModelId!}
          modelsKeyedById={modelsKeyedById}
          disableUpdate={savingInProgress}
          updateModel={updateModel}
          remapModel={remapModel}
        />
      </Modal>
    </Content>
  );
};

export default Models;
